import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { MessageSquare } from 'lucide-react';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const { systemName, systemLogo } = useSettings();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login({ email, password });
      navigate('/');
    } catch (err: any) {
      // Handle different types of errors
      if (!err.response) {
        // Network error or no response from server
        setError('Não foi possível conectar ao servidor. Verifique sua conexão ou tente novamente mais tarde.');
      } else if (err.response.status === 502) {
        setError('Servidor temporariamente indisponível (502 Bad Gateway). Tente novamente em alguns instantes.');
      } else if (err.response.status === 503) {
        setError('Serviço temporariamente indisponível (503). Tente novamente em alguns instantes.');
      } else if (err.response.status === 504) {
        setError('Tempo limite de conexão com o servidor (504 Gateway Timeout). Tente novamente.');
      } else if (err.response.status >= 500) {
        setError('Erro interno do servidor. Tente novamente mais tarde.');
      } else if (err.response.status === 403) {
        // Account deactivated
        setError(err.response?.data?.error || 'Sua conta foi desativada. Entre em contato com o administrador do sistema.');
      } else if (err.response.status === 401) {
        setError('Credenciais inválidas. Verifique seu email e senha.');
      } else {
        setError(err.response?.data?.error || 'Erro ao fazer login. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-xl p-8 border border-gray-700">
        <div className="flex flex-col items-center mb-8">
          {systemLogo ? (
            <div className="w-20 h-20 mb-4 flex items-center justify-center">
              <img
                src={`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}${systemLogo}`}
                alt="Logo"
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ) : (
            <div className="bg-blue-600 p-3 rounded-full mb-4">
              <MessageSquare className="w-8 h-8 text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-white">{systemName}</h1>
          <p className="text-gray-400 mt-2">Portal de Atendimento ao Cliente</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Endereço de Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition placeholder-gray-400"
              placeholder="agent@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
              Senha
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition placeholder-gray-400"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium"
          >
            {isLoading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        {import.meta.env.DEV && (
          <div className="mt-6 text-center text-sm text-gray-400">
            <p>Credenciais padrão (apenas desenvolvimento):</p>
            <p className="font-mono mt-1 text-gray-300">admin@example.com / admin123</p>
          </div>
        )}
      </div>
    </div>
  );
};
